import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../app/store/store';
import {
  setNotification,
  setErrorNotification,
} from '../../../app/store/notifications';
import {
  fetchHelpdeskSettings,
  updateHelpdeskSettings,
  fetchRoutingRules,
  createRoutingRule,
  updateRoutingRule,
  deleteRoutingRule,
  reorderRoutingRules,
} from '../../../entities/helpdesk-settings/model';
import { SettingsToggle } from '@shared/ui/settings-toggle';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { RoutingRule } from '../../../types/helpdeskSettingsInterfaces';

/* ── Описание правила (формируется из условий) ── */
const ruleDescription = (rule: RoutingRule): string => {
  const parts: string[] = [];
  if (rule.priority_filter) parts.push(`Приоритет: ${rule.priority_filter}`);
  if (rule.channel) parts.push(`Канал: ${rule.channel}`);
  if (rule.region_filter) parts.push(`Регион:${rule.region_filter}`);
  if (rule.keywords?.length) parts.push(`Ключевые слова в теме`);
  if (parts.length === 0) parts.push('Резервное правило');
  const assignee = rule.assign_to
    ? `${rule.assign_to.last_name} ${rule.assign_to.first_name?.[0] || ''}.`
    : 'свободного оператора';
  return `${parts.join(' · ')} > ${assignee}`;
};

/* ── Sortable-элемент правила ── */
interface SortableRuleProps {
  rule: RoutingRule;
  index: number;
  onEdit: (rule: RoutingRule) => void;
  onToggle: (rule: RoutingRule, active: boolean) => void;
}

const SortableRule: React.FC<SortableRuleProps> = ({ rule, index, onEdit, onToggle }) => {
  // dnd-kit (в этой версии) типизирует id как `string`, поэтому
  // приводим число к строке всюду, где id уходит в библиотеку.
  // Эквивалентность сохраняем за счёт того, что и SortableContext, и
  // обработчик handleDragEnd используют ту же строковую версию.
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: String(rule.id),
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    // transition приходит как string | null, а CSSProperties ждёт
    // string | undefined. null → undefined, иначе ругается TS.
    transition: transition ?? undefined,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-4 py-4 border-b border-gray-100 last:border-b-0"
    >
      {/* Drag handle */}
      <button {...attributes} {...listeners} className="cursor-grab text-gray-300 hover:text-gray-500 touch-none">
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path d="M7 2a2 2 0 10.001 4.001A2 2 0 007 2zm0 6a2 2 0 10.001 4.001A2 2 0 007 8zm0 6a2 2 0 10.001 4.001A2 2 0 007 14zm6-8a2 2 0 10-.001-4.001A2 2 0 0013 6zm0 2a2 2 0 10.001 4.001A2 2 0 0013 8zm0 6a2 2 0 10.001 4.001A2 2 0 0013 14z" />
        </svg>
      </button>

      {/* Number */}
      <span className="w-8 h-8 flex items-center justify-center bg-gray-100 text-sm font-semibold text-[#2d2c2c] rounded-full shrink-0">
        {index + 1}
      </span>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[#2d2c2c] truncate">{rule.name}</p>
        <p className="text-xs text-gray-400 truncate mt-0.5">{ruleDescription(rule)}</p>
      </div>

      {/* Toggle + Edit */}
      <div className="flex items-center gap-3 shrink-0">
        <button
          type="button"
          role="switch"
          aria-checked={rule.is_active}
          onClick={() => onToggle(rule, !rule.is_active)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
            rule.is_active ? 'bg-[#e3282a]' : 'bg-gray-300'
          }`}
        >
          <span className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
            rule.is_active ? 'translate-x-6' : 'translate-x-1'
          }`} />
        </button>
        <button onClick={() => onEdit(rule)}
          className="text-sm font-medium text-[#e3282a] hover:text-[#c82022] transition-colors">
          Изменить
        </button>
      </div>
    </div>
  );
};

/* ── Модалка создания/редактирования ── */
interface RuleModalProps {
  rule?: RoutingRule | null;
  onClose: () => void;
  onSave: (data: Partial<RoutingRule>) => void;
  onDelete?: () => void;
}

const RuleModal: React.FC<RuleModalProps> = ({ rule, onClose, onSave, onDelete }) => {
  const [name, setName] = useState(rule?.name || '');
  const [description, setDescription] = useState(rule?.description || '');
  const [channel, setChannel] = useState(rule?.channel || '');
  const [priority, setPriority] = useState(rule?.priority_filter || '');
  const [region, setRegion] = useState(rule?.region_filter || '');
  const [keywords, setKeywords] = useState(rule?.keywords?.join(', ') || '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSave({
      name: name.trim(),
      description: description.trim(),
      channel: channel as any,
      priority_filter: priority as any,
      region_filter: region,
      keywords: keywords ? keywords.split(',').map((k) => k.trim()).filter(Boolean) : [],
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-[#2d2c2c]">
            {rule ? 'Изменить сценарий' : 'Добавить сценарий'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Сценарий*</label>
            <input value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Кратко опишите сценарий..."
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Описание*</label>
            <input value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="Кратко опишите"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Канал</label>
              <select value={channel} onChange={(e) => setChannel(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-red-400">
                <option value="">Любой</option>
                <option value="email">Email</option>
                <option value="web">Веб-портал</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Приоритет</label>
              <select value={priority} onChange={(e) => setPriority(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-red-400">
                <option value="">Любой</option>
                <option value="critical">Критический</option>
                <option value="high">Высокий</option>
                <option value="medium">Средний</option>
                <option value="low">Низкий</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Регион</label>
            <input value={region} onChange={(e) => setRegion(e.target.value)}
              placeholder="ES, MX, CO, AR"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ключевые слова (через запятую)</label>
            <input value={keywords} onChange={(e) => setKeywords(e.target.value)}
              placeholder="BLE, TD-150"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400" />
          </div>

          <div className="flex justify-between pt-2">
            <div>
              {rule && onDelete && (
                <button type="button" onClick={onDelete}
                  className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-800">
                  Удалить
                </button>
              )}
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
                Отмена
              </button>
              <button type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-[#e3282a] rounded-lg hover:bg-[#c82022]">
                {rule ? 'Сохранить' : 'Добавить'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ── Основной экран ── */
const RoutingSettings: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { settings, status, routingRules, rulesStatus } = useSelector(
    (state: RootState) => state.helpdeskSettings,
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<RoutingRule | null>(null);
  const [localRules, setLocalRules] = useState<RoutingRule[]>([]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  useEffect(() => {
    if (status === 'idle') dispatch(fetchHelpdeskSettings());
    if (rulesStatus === 'idle') dispatch(fetchRoutingRules());
  }, [dispatch, status, rulesStatus]);

  useEffect(() => {
    setLocalRules(routingRules);
  }, [routingRules]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      // active.id / over.id у dnd-kit — UniqueIdentifier (string | number),
      // в этой версии приходит строкой. Сравниваем по строке, а индекс
      // ищем по числовой версии rule.id.
      const oldIndex = localRules.findIndex((r) => String(r.id) === String(active.id));
      const newIndex = localRules.findIndex((r) => String(r.id) === String(over.id));
      const reordered = arrayMove(localRules, oldIndex, newIndex);
      setLocalRules(reordered);
      dispatch(reorderRoutingRules(reordered.map((r) => r.id)))
        .unwrap()
        .then(() => dispatch(setNotification('Порядок сценариев обновлён')))
        .catch((err: unknown) => {
          dispatch(
            setErrorNotification(
              typeof err === 'string' ? err : 'Не удалось обновить порядок'
            )
          );
          // Откатываем оптимистичный порядок на сервиерный, чтобы UI
          // не висел в неправильном состоянии.
          setLocalRules(routingRules);
        });
    },
    [dispatch, localRules],
  );

  const handleToggle = useCallback(
    (rule: RoutingRule, active: boolean) => {
      dispatch(updateRoutingRule({ id: rule.id, data: { is_active: active } }));
    },
    [dispatch],
  );

  const handleSave = useCallback(
    async (data: Partial<RoutingRule>) => {
      const isEdit = Boolean(editingRule);
      try {
        if (editingRule) {
          await dispatch(
            updateRoutingRule({ id: editingRule.id, data })
          ).unwrap();
        } else {
          await dispatch(
            createRoutingRule({ ...data, order_index: localRules.length })
          ).unwrap();
        }
        // Тост даём ровно один — фронт раньше сохранял молча и было
        // непонятно, прошло ли действие.
        dispatch(
          setNotification(
            isEdit ? 'Сценарий обновлён' : 'Сценарий добавлен'
          )
        );
        setModalOpen(false);
        setEditingRule(null);
        dispatch(fetchRoutingRules());
      } catch (err: unknown) {
        dispatch(
          setErrorNotification(
            typeof err === 'string'
              ? err
              : isEdit
              ? 'Не удалось обновить сценарий'
              : 'Не удалось добавить сценарий'
          )
        );
      }
    },
    [dispatch, editingRule, localRules.length],
  );

  const handleDelete = useCallback(async () => {
    if (!editingRule) return;
    try {
      await dispatch(deleteRoutingRule(editingRule.id)).unwrap();
      dispatch(setNotification('Сценарий удалён'));
      setModalOpen(false);
      setEditingRule(null);
    } catch (err: unknown) {
      dispatch(
        setErrorNotification(
          typeof err === 'string' ? err : 'Не удалось удалить сценарий'
        )
      );
    }
  }, [dispatch, editingRule]);

  const handleSettingsUpdate = useCallback(
    (field: string, value: boolean) => {
      dispatch(updateHelpdeskSettings({ [field]: value }));
    },
    [dispatch],
  );

  if ((status === 'loading' && !settings) || (rulesStatus === 'loading' && localRules.length === 0)) {
    return (
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <div className="animate-pulse space-y-4">
          <div className="h-5 bg-gray-200 rounded w-1/3"></div>
          <div className="h-16 bg-gray-100 rounded"></div>
          <div className="h-16 bg-gray-100 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Сценарии маршрутизации.
          Заголовок «Сценарии маршрутизации» переехал в шапку приложения,
          здесь оставляем функциональную подсказку и action-кнопку. */}
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-gray-500">
            Заявки распределяются по первому подходящему правилу сверху вниз
          </p>
          <button onClick={() => { setEditingRule(null); setModalOpen(true); }}
            className="px-4 py-2 text-sm font-medium text-white bg-[#e3282a] rounded-lg hover:bg-[#c82022] transition-colors">
            + Добавить сценарий
          </button>
        </div>

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          {/* SortableContext.items в этой версии ждёт string[] —
              приводим явно. */}
          <SortableContext items={localRules.map((r) => String(r.id))} strategy={verticalListSortingStrategy}>
            {localRules.map((rule, idx) => (
              <SortableRule
                key={rule.id}
                rule={rule}
                index={idx}
                onEdit={(r) => { setEditingRule(r); setModalOpen(true); }}
                onToggle={handleToggle}
              />
            ))}
          </SortableContext>
        </DndContext>

        {localRules.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-8">
            Нет правил маршрутизации. Добавьте первый сценарий.
          </p>
        )}
      </div>

      {/* Очередь при отсутствии операторов */}
      {settings && (
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="text-base font-semibold text-[#2d2c2c] mb-1">
            Очередь при отсутствии операторов
          </h2>
          <p className="text-sm text-gray-400 mb-4">
            Если ни один оператор не доступен по расписанию
          </p>
          <div>
            <SettingsToggle
              label="Уведомить Администратора и Ведущего оператора"
              description="При попадании заявки в общую очередь"
              checked={settings.notify_admin_on_unassigned}
              onChange={(v) => handleSettingsUpdate('notify_admin_on_unassigned', v)}
            />
            <SettingsToggle
              label="Автоназначение при появлении свободного оператора"
              description="Заявка из очереди назначается автоматически"
              checked={settings.auto_assign_on_available}
              onChange={(v) => handleSettingsUpdate('auto_assign_on_available', v)}
            />
          </div>
        </div>
      )}

      {/* Модалка */}
      {modalOpen && (
        <RuleModal
          rule={editingRule}
          onClose={() => { setModalOpen(false); setEditingRule(null); }}
          onSave={handleSave}
          onDelete={editingRule ? handleDelete : undefined}
        />
      )}
    </div>
  );
};

export default RoutingSettings;
